import { useEffect, useState } from "react";

import { BrowserRouter } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { AppRoutes } from "~/components/AppRoutes";
import { RoutedErrorBoundary } from "~/components/RoutedErrorBoundary";
import { SentryErrorBoundary } from "~/components/SentryErrorBoundary";
import ProfileProvider from "~/components/context/ProfileProvider";
import { Toaster as Sonner } from "~/components/ui/sonner";
import { Toaster } from "~/components/ui/toaster";
import { TooltipProvider } from "~/components/ui/tooltip";
import { initPostHog } from "~/integrations/posthog/client";
import { supabase } from "~/integrations/supabase/client";
import { handleError } from "~/lib/errorHandler";

const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      onError: (error, variables, context) => {
        // Only log if not already handled by component
        // Components should set error.__handled = true when they handle errors explicitly
        if (!error || !(error as any).__handled) {
          handleError(error, {
            context: "TanStack Query mutation",
            metadata: { variables, context },
          });
        }
      },
    },
  },
});

const App = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Initialize PostHog
    initPostHog();

    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      setIsAuthenticated(!!data.session);
      setIsLoading(false);
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session);
      setIsLoading(false);
    });

    checkAuth();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        Cargando...
      </div>
    );
  }

  return (
    <SentryErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <ProfileProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <RoutedErrorBoundary>
                <AppRoutes isAuthenticated={isAuthenticated} />
              </RoutedErrorBoundary>
            </BrowserRouter>
          </ProfileProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </SentryErrorBoundary>
  );
};

export default App;
